import { useMemo, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight,
  Search,
} from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import apiClient from '@/api/client'
import type { UseReportRange } from '@/hooks/useReportRange'
import type { RefundDailyRow } from '@/types'
import { useCurrency } from '@/contexts/CurrencyContext'
import { ReportsExportSlot } from './ReportsExportSlot'
import { ExportButton } from './ExportButton'
import { DayFilter } from './DayFilter'
import { RefundsBrowserTab } from './RefundsBrowserTab'
import { openPrintableReport, escapeHtml } from '@/lib/printReport'
import { useBusinessNameWithFallback } from '@/hooks/useBusinessName'
import { formatDateHuman, formatDateRangeHuman, toIsoDate } from '@/lib/utils'
import type { ReportTab, ReportsSearch } from './reportsRouteSearch'

type SortKey = 'date' | 'refunds' | 'gross' | 'tax' | 'failed'
type SortDir = 'asc' | 'desc'

interface Props {
  range: UseReportRange
}

/**
 * Refund Logs tab — sortable per-day rollup of refunds (credit notes) with a
 * drill-in drawer that lists each refund and lets the operator open the
 * printable refund/credit-note receipt. Mirrors the Daily Sales UX.
 *
 * Day bucketing is by *issue date* (when the credit note was created),
 * matching the backend GetRefundsDaily query. This is the audit-log
 * interpretation: refunding a 3-day-old order shows under today.
 */
export function RefundLogsTab({ range }: Props) {
  const { formatCurrency } = useCurrency()
  const navigate = useNavigate()
  const search = useSearch({ from: '/admin/reports' }) as ReportsSearch
  const [textSearch, setTextSearch] = useState('')
  const [sortKey, setSortKey] = useState<SortKey>('date')
  const [sortDir, setSortDir] = useState<SortDir>('desc')

  const detailDay = useMemo(() => {
    const d = search.day
    if (!d || !/^\d{4}-\d{2}-\d{2}$/.test(d)) return null
    const dt = new Date(`${d}T00:00:00`)
    return Number.isNaN(dt.getTime()) ? null : dt
  }, [search.day])

  const openDrill = (iso: string) => {
    navigate({
      to: '/admin/reports',
      search: (prev): ReportsSearch => ({
        ...(prev as ReportsSearch),
        tab: 'refunds' as ReportTab,
        day: iso,
      }),
      replace: true,
    })
  }

  const closeDrill = () => {
    navigate({
      to: '/admin/reports',
      search: (prev): ReportsSearch => {
        const { day: _omit, ...rest } = prev as ReportsSearch
        return rest
      },
      replace: true,
    })
  }

  const setDrillDay = (d: Date) => {
    navigate({
      to: '/admin/reports',
      search: (prev): ReportsSearch => ({
        ...(prev as ReportsSearch),
        tab: 'refunds' as ReportTab,
        day: toIsoDate(d),
      }),
      replace: true,
    })
  }

  const shiftDrillDay = (offset: number) => {
    if (!detailDay) return
    const next = new Date(detailDay)
    next.setDate(next.getDate() + offset)
    next.setHours(0, 0, 0, 0)
    setDrillDay(next)
  }
  const todayIso = useMemo(() => toIsoDate(new Date()), [])
  const nextDisabled = useMemo(() => {
    if (!detailDay) return true
    const next = new Date(detailDay)
    next.setDate(next.getDate() + 1)
    return toIsoDate(next) > todayIso
  }, [detailDay, todayIso])

  const refundsQuery = useQuery({
    queryKey: ['reports-v2', 'refunds-daily', range.fromISO, range.toISO],
    queryFn: () => apiClient.getRefundsDaily(range.fromISO, range.toISO),
    staleTime: 30_000,
  })

  const allRows = refundsQuery.data?.data ?? []

  const detailRow = useMemo(() => {
    if (!detailDay) return null
    const iso = toIsoDate(detailDay)
    return allRows.find((r) => r.date === iso) ?? null
  }, [allRows, detailDay])

  const sorted = useMemo(() => {
    const q = textSearch.trim().toLowerCase()
    const filtered = q
      ? allRows.filter((r) => {
          const human = formatDateHuman(r.date).toLowerCase()
          return (
            r.date_label.toLowerCase().includes(q) ||
            r.date.toLowerCase().includes(q) ||
            human.includes(q)
          )
        })
      : allRows
    const dir = sortDir === 'asc' ? 1 : -1
    const copy = [...filtered]
    const valueFor = (r: RefundDailyRow): string | number => {
      switch (sortKey) {
        case 'date':
          return r.date
        case 'refunds':
          return r.refunds
        case 'gross':
          return r.gross_refund
        case 'tax':
          return r.tax_refund
        case 'failed':
          return r.failed
        default:
          return 0
      }
    }
    copy.sort((a, b) => {
      const av = valueFor(a)
      const bv = valueFor(b)
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * dir
      return ((av as number) - (bv as number)) * dir
    })
    return copy
  }, [allRows, textSearch, sortKey, sortDir])

  const totals = useMemo(() => {
    return sorted.reduce(
      (acc, r) => ({
        refunds: acc.refunds + r.refunds,
        gross: acc.gross + r.gross_refund,
        subtotal: acc.subtotal + r.subtotal_refund,
        tax: acc.tax + r.tax_refund,
        synced: acc.synced + r.synced,
        pending: acc.pending + r.pending,
        failed: acc.failed + r.failed,
      }),
      { refunds: 0, gross: 0, subtotal: 0, tax: 0, synced: 0, pending: 0, failed: 0 },
    )
  }, [sorted])

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else {
      setSortKey(k)
      setSortDir('desc')
    }
  }

  const headerSort = (label: string, k: SortKey, alignRight = false) => (
    <button
      type="button"
      onClick={() => toggleSort(k)}
      className={`group inline-flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground hover:text-foreground ${alignRight ? 'justify-end w-full' : ''}`}
    >
      {label}
      {sortKey === k ? (
        sortDir === 'asc' ? (
          <ArrowUp className="w-3 h-3" />
        ) : (
          <ArrowDown className="w-3 h-3" />
        )
      ) : (
        <ArrowUpDown className="w-3 h-3 opacity-40 group-hover:opacity-80" />
      )}
    </button>
  )

  const brand = useBusinessNameWithFallback()
  const handlePrintPdf = () => {
    openPrintableReport({
      title: 'Refund Logs',
      subtitle: `${formatDateRangeHuman(range.from, range.to)} • Asia/Karachi`,
      bodyHtml: buildRefundsPdf(sorted, totals, formatCurrency),
      brand,
    })
  }

  return (
    <div className="space-y-4">
      <div className="relative max-w-xs w-full">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search a day (e.g. Fri, 9 May)"
          value={textSearch}
          onChange={(e) => setTextSearch(e.target.value)}
          className="pl-9"
        />
      </div>
      <ReportsExportSlot>
        <ExportButton
          report="refunds"
          reportLabel="Refunds"
          fromISO={range.fromISO}
          toISO={range.toISO}
          onPrintPdf={handlePrintPdf}
        />
      </ReportsExportSlot>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Refund Logs — {formatDateRangeHuman(range.from, range.to)}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {refundsQuery.isLoading ? (
            <div className="p-6 space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : sorted.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No refunds in this range.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2.5">{headerSort('Date', 'date')}</th>
                    <th className="text-right px-4 py-2.5">{headerSort('Refunds', 'refunds', true)}</th>
                    <th
                      className="text-right px-4 py-2.5"
                      title="SUM(total_amount) of credit notes issued on this day"
                    >
                      {headerSort('Gross refund', 'gross', true)}
                    </th>
                    <th className="text-right px-4 py-2.5">{headerSort('Tax refund', 'tax', true)}</th>
                    <th
                      className="text-right px-4 py-2.5"
                      title="Count of credit notes whose PRAL/FBR submission failed"
                    >
                      {headerSort('Failed', 'failed', true)}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {sorted.map((row) => (
                    <RefundDailyTableRow
                      key={row.date}
                      row={row}
                      formatCurrency={formatCurrency}
                      onDrillIn={() => openDrill(row.date)}
                    />
                  ))}
                </tbody>
                <tfoot className="bg-muted/30 font-semibold">
                  <tr>
                    <td className="px-4 py-3">Total</td>
                    <td className="text-right tabular-nums px-4 py-3">{totals.refunds.toLocaleString('en-US')}</td>
                    <td className="text-right tabular-nums px-4 py-3 text-red-700 dark:text-red-300">
                      {totals.gross > 0 ? `−${formatCurrency(totals.gross)}` : formatCurrency(0)}
                    </td>
                    <td className="text-right tabular-nums px-4 py-3">{formatCurrency(totals.tax)}</td>
                    <td className="text-right tabular-nums px-4 py-3">
                      {totals.failed > 0 ? totals.failed.toLocaleString('en-US') : '—'}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Sheet
        open={!!detailDay}
        onOpenChange={(open) => {
          if (!open) closeDrill()
        }}
      >
        <SheetContent size="xl" aria-describedby={undefined} className="p-0">
          {detailDay && (
            <>
              <SheetHeader className="gap-3">
                <div className="flex items-center justify-between gap-3 pr-9">
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => shiftDrillDay(-1)}
                      aria-label="Previous day"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <SheetTitle className="text-base">{formatDateHuman(detailDay)}</SheetTitle>
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => shiftDrillDay(1)}
                      disabled={nextDisabled}
                      aria-label="Next day"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <DayFilter day={detailDay} onChange={setDrillDay} />
                </div>
                {detailRow && (
                  <DaySummaryStrip row={detailRow} formatCurrency={formatCurrency} />
                )}
              </SheetHeader>
              <SheetBody>
                <RefundsBrowserTab day={detailDay} />
              </SheetBody>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  )
}

function DaySummaryStrip({
  row,
  formatCurrency,
}: {
  row: RefundDailyRow
  formatCurrency: (n: number) => string
}) {
  const stats: { label: string; value: string; emphasised?: boolean; tone?: 'danger' }[] = [
    {
      label: 'Gross refund',
      value: row.gross_refund > 0 ? `−${formatCurrency(row.gross_refund)}` : formatCurrency(0),
      emphasised: true,
      tone: 'danger',
    },
    { label: 'Refunds', value: row.refunds.toLocaleString('en-US') },
    { label: 'Synced', value: row.synced.toLocaleString('en-US') },
    { label: 'Pending', value: row.pending.toLocaleString('en-US') },
    {
      label: 'Failed',
      value: row.failed.toLocaleString('en-US'),
      tone: row.failed > 0 ? 'danger' : undefined,
    },
  ]
  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
      {stats.map((s) => (
        <div key={s.label} className="rounded-md bg-muted/50 px-3 py-2">
          <p className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            {s.label}
          </p>
          <p
            className={`tabular-nums text-sm ${s.emphasised ? 'font-semibold' : ''} ${
              s.tone === 'danger' ? 'text-red-700 dark:text-red-300' : 'text-foreground'
            }`}
          >
            {s.value}
          </p>
        </div>
      ))}
    </div>
  )
}

function RefundDailyTableRow({
  row,
  formatCurrency,
  onDrillIn,
}: {
  row: RefundDailyRow
  formatCurrency: (n: number) => string
  onDrillIn: () => void
}) {
  const label = formatDateHuman(row.date)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTableRowElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onDrillIn()
    }
  }
  return (
    <tr
      role="button"
      tabIndex={0}
      aria-label={`Open refund details for ${label}`}
      onClick={onDrillIn}
      onKeyDown={handleKeyDown}
      className="group cursor-pointer transition-colors hover:bg-muted/40 focus:outline-none focus-visible:bg-muted/40 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
    >
      <td className="px-4 py-3 font-medium tabular-nums">
        <div className="flex items-center gap-2">
          <span>{label}</span>
          <ChevronRight
            className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground"
            aria-hidden="true"
          />
        </div>
      </td>
      <td className="text-right tabular-nums px-4 py-3">{row.refunds.toLocaleString('en-US')}</td>
      <td className="text-right tabular-nums px-4 py-3 font-semibold text-red-700 dark:text-red-300">
        {row.gross_refund > 0 ? `−${formatCurrency(row.gross_refund)}` : '—'}
      </td>
      <td className="text-right tabular-nums px-4 py-3 text-muted-foreground">
        {row.tax_refund > 0 ? formatCurrency(row.tax_refund) : '—'}
      </td>
      <td className="text-right tabular-nums px-4 py-3">
        {row.failed > 0 ? (
          <span className="text-red-700 dark:text-red-300 font-semibold">{row.failed}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  )
}

function buildRefundsPdf(
  rows: RefundDailyRow[],
  totals: {
    refunds: number
    gross: number
    tax: number
    synced: number
    pending: number
    failed: number
  },
  formatCurrency: (n: number) => string,
): string {
  const body = rows
    .map(
      (r) =>
        `<tr><td>${escapeHtml(formatDateHuman(r.date))}</td><td class="num">${r.refunds}</td><td class="num">${escapeHtml(formatCurrency(r.gross_refund))}</td><td class="num">${escapeHtml(formatCurrency(r.tax_refund))}</td><td class="num">${r.synced}</td><td class="num">${r.pending}</td><td class="num">${r.failed}</td></tr>`,
    )
    .join('')
  return `
    <table>
      <thead><tr>
        <th>Date</th><th class="num">Refunds</th><th class="num">Gross refund</th>
        <th class="num">Tax refund</th><th class="num">Synced</th><th class="num">Pending</th><th class="num">Failed</th>
      </tr></thead>
      <tbody>${body || `<tr><td colspan="7" class="muted">No refunds</td></tr>`}</tbody>
      <tfoot><tr>
        <th>Total</th>
        <th class="num">${totals.refunds}</th>
        <th class="num">${escapeHtml(formatCurrency(totals.gross))}</th>
        <th class="num">${escapeHtml(formatCurrency(totals.tax))}</th>
        <th class="num">${totals.synced}</th>
        <th class="num">${totals.pending}</th>
        <th class="num">${totals.failed}</th>
      </tr></tfoot>
    </table>
  `
}
